import React, { forwardRef, memo } from 'react';
import cn from 'classnames';
import { createSrc } from 'js/responsive-images';
import OPE from 'js/on-page-editing';
import { defineTheme, ThemeKeys } from 'js/utils/theme-helpers';

import { IImage } from 'source/NFR.types';
import './image.scss';

const themes = defineTheme({
  roundedSmall: 'fluid-image--theme-rounded-small',
  rounded: 'fluid-image--theme-rounded',
  //following themes are used in print
  anchored: 'fluid-image--theme-anchored',
  mosaic: 'fluid-image--theme-mosaic'
});
export type ImageTheme = ThemeKeys<typeof themes>;

export const MAX_VIEWPORT_WIDTH = 5120;
export const MAX_VIEWPORT_HEIGHT = 2160;

const MIN_BASE_DEFAULT = 320;
const MAX_ALLOWED_WIDTH = 2600;
const MAX_DPR = 2.0;

/**
 * Generate evenly distributed widths for srcset.
 * These widths are optimized for byte-budget and caching efficiency.
 */
function generateAllowedWidths(): number[] {
  return [480, 768, 1024, 1280, 1440, 2048];
}

function buildWidthSrcSet(
  baseSrc: string,
  minWidth: number | null | undefined,
  maxWidth: number | null | undefined,
  aspectRatio: number
): string {
  const MIN_BASE = MIN_BASE_DEFAULT;

  // Determine effective max: clamp requested max to global MAX_ALLOWED_WIDTH, ensure at least MIN_BASE
  const requested = typeof maxWidth === 'number' && maxWidth > 0 ? Math.round(maxWidth) : MAX_ALLOWED_WIDTH;

  const effectiveMax = Math.min(MAX_ALLOWED_WIDTH, requested) ?? MIN_BASE;

  // Generate allowed widths using byte-budget algorithm + DPR multiples
  const allowedWidths = generateAllowedWidths();

  // Filter to respect minWidth and maxWidth bounds
  const filteredWidths = allowedWidths.filter((w, i) => {
    if (minWidth && w < minWidth) return false;
    if (w > effectiveMax * MAX_DPR && allowedWidths[i - 1] > effectiveMax * MAX_DPR) return false;
    return true;
  });

  // If no widths in range, return at least the effective max
  if (filteredWidths.length === 0) {
    return `${createSrc(baseSrc, effectiveMax, aspectRatio)} ${effectiveMax}w`;
  }

  return filteredWidths.map(w => `${createSrc(baseSrc, w, aspectRatio)} ${w}w`).join(', ');
}

export interface ImageProps extends Omit<IImage, 'dest' | 'sizes' | 'srcSet'> {
  className?: string;
  dest?: string;
  sizes?: string;
  srcSet?: string;
  initialSize?: number;
  responsive?: boolean;
  theme?: ImageTheme | ImageTheme[];
  fetchPriority?: 'auto' | 'high' | 'low' | undefined;
  loading?: 'eager' | 'lazy';
  darkFilter?: boolean;
  isArrowIcon?: boolean;
  width?: number | string;
  height?: number | string;
  sources?: PictureSource[];
}

type FocusPoint = { x: number; y: number };

type PictureSource = React.SourceHTMLAttributes<HTMLSourceElement> & {
  src?: string;
};

// eslint-disable-next-line react/no-multi-comp
const Image = memo(
  forwardRef<HTMLImageElement, ImageProps>(
    (
      {
        alt,
        loading,
        sizes,
        src: imgSrc,
        sources,
        width,
        theme,
        onPageEditing,
        isArrowIcon = false as unknown as boolean,
        isFluid = false as unknown as boolean,
        height,
        minWidth,
        maxWidth,
        aspectRatio,
        className,
        focusPoint,
        darkFilter,
        ...props
      },
      ref
    ) => {
      if (!imgSrc || (typeof imgSrc === 'string' && !imgSrc.trim())) {
        return null;
      }

      // Calculate width and height attributes
      const imgWidth = width as unknown as number | string | undefined;
      const imgHeight = height as unknown as number | string | undefined;

      const effectiveBaseWidth =
        typeof width === 'number' && width > 0 ? Math.min(width, MAX_ALLOWED_WIDTH) : MAX_ALLOWED_WIDTH;

      const effectiveMaxWidth = maxWidth ?? width;

      const effectiveMinWidth = (minWidth ?? (isFluid && MIN_BASE_DEFAULT * MAX_DPR) ?? null) as
        | number
        | null
        | undefined;

      const effectiveLoadingValue = props.fetchPriority === 'high' ? 'eager' : (loading ?? 'lazy');

      const effectiveFetchPriority = props.fetchPriority;

      const effectiveAspectRatio = aspectRatio ?? (imgWidth && imgHeight ? Number(imgWidth) / Number(imgHeight) : 1);

      const imageSrc = imgSrc
        ? createSrc(imgSrc, (effectiveMaxWidth as number | undefined) ?? effectiveBaseWidth, effectiveAspectRatio)
        : '';

      const effectiveSizes = sizes;

      const effectiveSrcSet =
        props.srcSet ??
        buildWidthSrcSet(
          imgSrc,
          effectiveMinWidth,
          (typeof effectiveMaxWidth === 'number' ? effectiveMaxWidth : 0) ?? 0,
          effectiveAspectRatio
        );

      function renderPicture() {
        return (
          <picture
            className={cn('fluid-image', className, themes(theme), {
              'fluid-image--arrow-icon': isArrowIcon
            })}
            {...(OPE(onPageEditing?.image) as Record<string, unknown>)}
          >
            {Array.isArray(sources)
              ? sources.map((source, i) => {
                  const { src, srcSet, ...sourceAttributes } = source;

                  const widthVal = sourceAttributes.width ?? 0;
                  const heightVal = sourceAttributes.height ?? 0;
                  const widthNum = Number(widthVal);
                  const heightNum = Number(heightVal);

                  const aspectRatioForSource = heightNum > 0 ? (widthNum > 0 ? widthNum / heightNum : 0) : -1;

                  return (
                    <source
                      key={source.media ?? i}
                      {...sourceAttributes}
                      srcSet={
                        srcSet ??
                        buildWidthSrcSet(
                          imgSrc,
                          effectiveMinWidth,
                          widthNum > 0
                            ? widthNum
                            : ((typeof effectiveMaxWidth === 'number' ? effectiveMaxWidth : 0) ?? 0),
                          aspectRatioForSource
                        )
                      }
                    />
                  );
                })
              : null}

            <img
              loading={effectiveLoadingValue}
              sizes={effectiveSizes}
              className={cn('fluid-image', className, themes(theme), {
                'fluid-image--arrow-icon': isArrowIcon,
                'fluid-image--image': isFluid,
                'fluid-image--dark-filter': darkFilter
              })}
              fetchPriority={effectiveFetchPriority}
              ref={ref}
              alt={alt ?? ''}
              width={imgWidth}
              height={imgHeight}
              style={
                (focusPoint as FocusPoint | undefined)
                  ? {
                      objectPosition: `${(focusPoint as FocusPoint).x}% ${(focusPoint as FocusPoint).y}%`
                    }
                  : undefined
              }
              {...(props as Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet'>)}
              srcSet={effectiveSrcSet}
              src={imageSrc} // ensure src loads last if React is re-rendering, to allow some browsers to parse the loading attribute before deciding whether to load src
            />
          </picture>
        );
      }

      return renderPicture();
    }
  )
);

Image.displayName = 'Image';

export default Image;
